import { useEffect, useState, useCallback, useRef, Fragment } from 'react';
import { Plus, Edit2, Trash2, Power, ChevronRight, ChevronDown, FileText, MoreVertical, BarChart3, Tags, Eye, Loader2, Send } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { Modal } from '../../components/Modal';
import { formatMoeda } from '@fiodecobre/shared';
import { PromocaoFormModal } from './PromocaoFormModal';
import { FotoProduto } from '../../components/FotoProduto';
import { WhatsAppPickerInput } from '../../components/inputs/WhatsAppPickerInput';

interface Grupo {
  sigla: string;
  label_chatfc: string;
  nome: string;
  ativo: boolean;
  ordem: number;
}

interface PromocaoListagem {
  id: number;
  nome: string;
  grupo: string;
  grupo_nome: string | null;
  ativo: boolean;
  qtd_produtos: number;
  menor_estoque: string;
  fotos: (string | null)[];
}

interface ProdutoPromo {
  ordem: number;
  produto_id: number;
  codigo_interno: string | null;
  descricao: string;
  preco_pix: string;
  foto_url: string | null;
  estoque_atual: string;
}

export function PromocoesPage() {
  const { usuario } = useAuth();
  const toast = useToast();
  const dialog = useDialog();
  const ehAdmin = usuario?.perfil === 'admin';
  const ehGerente = ehAdmin || usuario?.perfil === 'gerente';

  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [lista, setLista] = useState<PromocaoListagem[]>([]);
  const [filtroGrupo, setFiltroGrupo] = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState<'' | 'true' | 'false'>('');
  const [carregando, setCarregando] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [expandidos, setExpandidos] = useState<Set<number>>(new Set());
  const [detalhes, setDetalhes] = useState<Record<number, ProdutoPromo[]>>({});
  const [menuAberto, setMenuAberto] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  // Preview de imagem (fly da promocao ou catalogo completo) — gerado on-demand no backend.
  const [previewImg, setPreviewImg] = useState<string | null>(null);
  const [previewTitulo, setPreviewTitulo] = useState('');
  const [gerando, setGerando] = useState<string | null>(null);
  // Envio de fly de teste — manda o fly real pra um numero escolhido (ignora filtro de equipe).
  const [testePromo, setTestePromo] = useState<PromocaoListagem | null>(null);
  const [numeroTeste, setNumeroTeste] = useState('');
  const [enviandoTeste, setEnviandoTeste] = useState(false);

  useEffect(() => {
    if (!menuAberto) return;
    function fora(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuAberto(false);
    }
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [menuAberto]);

  useEffect(() => {
    api.get<Grupo[]>('/api/grupos-promocao').then(setGrupos).catch(() => setGrupos([]));
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const params = new URLSearchParams();
      if (filtroGrupo) params.set('grupo', filtroGrupo);
      if (filtroAtivo) params.set('ativo', filtroAtivo);
      const qs = params.toString();
      const r = await api.get<PromocaoListagem[]>(`/api/promocoes${qs ? '?' + qs : ''}`);
      setLista(r || []);
    } catch (err: any) {
      toast.erro(err?.message || 'Erro ao carregar promocoes');
    } finally {
      setCarregando(false);
    }
  }, [filtroGrupo, filtroAtivo, toast]);

  useEffect(() => { carregar(); }, [carregar]);

  async function toggleExpandir(p: PromocaoListagem) {
    setExpandidos((curr) => {
      const novo = new Set(curr);
      if (novo.has(p.id)) novo.delete(p.id); else novo.add(p.id);
      return novo;
    });
    if (!detalhes[p.id]) {
      try {
        const d = await api.get<any>(`/api/promocoes/${p.id}`);
        setDetalhes((curr) => ({ ...curr, [p.id]: d.produtos || [] }));
      } catch (err: any) {
        toast.erro(err?.message || 'Erro ao carregar produtos');
      }
    }
  }

  async function alternarAtivo(p: PromocaoListagem) {
    try {
      await api.patch(`/api/promocoes/${p.id}/ativar`, { ativo: !p.ativo });
      carregar();
    } catch (err: any) {
      toast.erro(err?.message || 'Erro ao alterar');
    }
  }

  async function excluir(p: PromocaoListagem) {
    const ok = await dialog.confirmar(`Excluir a promocao "${p.nome}"?`, { perigoso: true });
    if (!ok) return;
    try {
      await api.delete(`/api/promocoes/${p.id}`);
      toast.sucesso('Promocao excluida');
      carregar();
    } catch (err: any) {
      toast.erro(err?.message || 'Erro ao excluir');
    }
  }

  function novaPromo() { setEditandoId(null); setModalAberto(true); }
  function editar(id: number) { setEditandoId(id); setModalAberto(true); }

  function emBreve() {
    toast.aviso('Disponivel na proxima onda (geracao de imagem)');
  }

  async function preVisualizarCatalogo() {
    setGerando('catalogo');
    try {
      const r = await api.get<{ jpg_base64: string; total_produtos: number }>('/api/promocoes/preview-catalogo');
      setPreviewTitulo(`Catalogo completo — ${r.total_produtos} produto(s)`);
      setPreviewImg(r.jpg_base64);
    } catch (err: any) {
      toast.erro(err?.message || 'Erro ao gerar o catalogo');
    } finally {
      setGerando(null);
    }
  }

  async function enviarTeste() {
    if (!testePromo) return;
    const digits = numeroTeste.replace(/\D/g, '');
    if (digits.length < 10) { toast.erro('Informe DDD + numero (ex: 64999998888).'); return; }
    setEnviandoTeste(true);
    try {
      await api.post(`/api/promocoes/${testePromo.id}/enviar-fly-teste`, { numero: digits });
      toast.sucesso(`Fly de teste enviado para ${numeroTeste}`);
      setTestePromo(null);
      setNumeroTeste('');
    } catch (err: any) {
      toast.erro(err?.message || 'Erro ao enviar o fly de teste');
    } finally {
      setEnviandoTeste(false);
    }
  }

  async function preVisualizarFly(p: PromocaoListagem) {
    setGerando(`fly-${p.id}`);
    try {
      const r = await api.get<{ jpg_base64: string }>(`/api/promocoes/${p.id}/preview`);
      setPreviewTitulo(`Fly — ${p.nome}`);
      setPreviewImg(r.jpg_base64);
    } catch (err: any) {
      toast.erro(err?.message || 'Erro ao gerar o fly');
    } finally {
      setGerando(null);
    }
  }

  return (
    <div>
      {/* Header: Titulo + Menu 3-dots + Botoes principais */}
      <div className="pagina-header">
        <h2 className="pagina-titulo" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          Promocoes
          {ehGerente && (
            <div ref={menuRef} style={{ position: 'relative' }}>
              <button onClick={() => setMenuAberto((v) => !v)}
                title="Mais opcoes"
                style={{
                  padding: 6, background: 'transparent', border: 'none', cursor: 'pointer',
                  color: '#6b7280', borderRadius: 4,
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                <MoreVertical size={18} />
              </button>
              {menuAberto && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, marginTop: 4, minWidth: 220,
                  background: 'white', border: '1px solid #e5e7eb', borderRadius: 6,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 30, padding: 4,
                }}>
                  <button onClick={() => { setMenuAberto(false); emBreve(); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 4, fontSize: 13, color: '#374151' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <BarChart3 size={14} /> Estatisticas de Disparos
                  </button>
                  <button onClick={() => { setMenuAberto(false); emBreve(); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 4, fontSize: 13, color: '#374151' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                    <Tags size={14} /> Grupos Validos
                  </button>
                </div>
              )}
            </div>
          )}
        </h2>
        {ehGerente && (
          <div className="pagina-acoes">
            <button onClick={preVisualizarCatalogo}
              disabled={gerando === 'catalogo'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                background: '#f97316', color: 'white', padding: '8px 16px',
                borderRadius: 6, border: 'none', cursor: gerando === 'catalogo' ? 'wait' : 'pointer',
                fontSize: 14, fontWeight: 500, opacity: gerando === 'catalogo' ? 0.6 : 1 }}
              title="Gera o JPG do catalogo completo (todos os produtos em promocao)">
              {gerando === 'catalogo'
                ? <Loader2 size={16} className="animate-spin" />
                : <FileText size={16} />}
              Pre-visualizar Catalogo
            </button>
            <button onClick={novaPromo}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'var(--primary)', color: 'white', padding: '8px 16px',
                borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
              <Plus size={16} /> Nova Promocao
            </button>
          </div>
        )}
      </div>

      {/* Filtros com labels acima */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Grupo</label>
          <select value={filtroGrupo} onChange={(e) => setFiltroGrupo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white" style={{ minWidth: 180 }}>
            <option value="">Todos</option>
            {grupos.map((g) => (
              <option key={g.sigla} value={g.sigla}>{g.nome || g.sigla}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Status</label>
          <select value={filtroAtivo} onChange={(e) => setFiltroAtivo(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white" style={{ minWidth: 140 }}>
            <option value="">Todos</option>
            <option value="true">So ativos</option>
            <option value="false">So inativos</option>
          </select>
        </div>
        <div style={{ marginLeft: 'auto', alignSelf: 'flex-end', fontSize: 13, color: '#6b7280', paddingBottom: 8 }}>
          {carregando ? 'Carregando…' : `${lista.length} promocao(oes)`}
        </div>
      </div>

      {/* Tabela */}
      <div style={{ background: 'white', borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#fed7aa', color: '#7c2d12' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, width: 36 }}></th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>NOME</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, width: 200 }}>GRUPO</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, width: 100 }}>STATUS</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, width: 100 }}>PRODUTOS</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, width: 140 }}>MENOR ESTOQUE</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, width: 140 }}>ACOES</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((p) => {
              const aberto = expandidos.has(p.id);
              const produtos = detalhes[p.id] || [];
              return (
                <Fragment key={p.id}>
                  <tr style={{ borderTop: '1px solid #f3f4f6', opacity: p.ativo ? 1 : 0.55 }}>
                    <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                      <button onClick={() => toggleExpandir(p)}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, color: '#6b7280' }}
                        title={aberto ? 'Recolher' : 'Ver produtos'}>
                        {aberto ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      </button>
                    </td>
                    <td style={{ padding: '12px', color: '#111827' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{p.nome}</span>
                        {p.fotos && p.fotos.length > 0 && (
                          <div style={{ display: 'flex', gap: 4 }}>
                            {p.fotos.map((f, i) => (
                              f
                                ? <FotoProduto key={i} url={f} tam={38} title="Produto da promocao" />
                                : <div key={i} title="Produto sem foto"
                                    style={{ width: 38, height: 38, background: '#f3f4f6', borderRadius: 4, border: '1px solid #e5e7eb' }} />
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ background: 'var(--primary-soft)', color: 'var(--primary)', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, marginRight: 8 }}>
                        {p.grupo}
                      </span>
                      <span style={{ fontSize: 13, color: '#4b5563' }}>{p.grupo_nome || ''}</span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {p.ativo
                        ? <span style={{ background: '#dcfce7', color: '#166534', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>Ativa</span>
                        : <span style={{ background: '#f3f4f6', color: '#6b7280', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>Inativa</span>}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', fontSize: 14, fontWeight: 600,
                      color: p.qtd_produtos === 3 ? '#15803d' : '#b91c1c' }}>
                      {p.qtd_produtos}/3
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', fontSize: 14,
                      color: Number(p.menor_estoque) > 0 ? '#111827' : '#b91c1c', fontWeight: 600 }}>
                      {Number(p.menor_estoque).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {ehGerente && (
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          <button onClick={() => preVisualizarFly(p)}
                            disabled={p.qtd_produtos !== 3 || gerando === `fly-${p.id}`}
                            title={p.qtd_produtos !== 3 ? 'Precisa de 3 produtos pra gerar o fly' : 'Pre-visualizar fly'}
                            style={{ background: '#fef9c3', color: '#854d0e', padding: 6, borderRadius: 4, border: 'none',
                              cursor: p.qtd_produtos !== 3 ? 'not-allowed' : (gerando === `fly-${p.id}` ? 'wait' : 'pointer'),
                              opacity: p.qtd_produtos !== 3 ? 0.4 : 1 }}>
                            {gerando === `fly-${p.id}`
                              ? <Loader2 size={14} className="animate-spin" />
                              : <Eye size={14} />}
                          </button>
                          <button onClick={() => { setTestePromo(p); setNumeroTeste(''); }}
                            disabled={p.qtd_produtos !== 3}
                            title={p.qtd_produtos !== 3 ? 'Precisa de 3 produtos pra gerar o fly' : 'Enviar fly de teste pra um numero'}
                            style={{ background: '#dcfce7', color: '#15803d', padding: 6, borderRadius: 4, border: 'none',
                              cursor: p.qtd_produtos !== 3 ? 'not-allowed' : 'pointer', opacity: p.qtd_produtos !== 3 ? 0.4 : 1 }}>
                            <Send size={14} />
                          </button>
                          <button onClick={() => editar(p.id)} title="Editar"
                            style={{ background: 'var(--primary-soft)', color: 'var(--primary)', padding: 6, borderRadius: 4, border: 'none', cursor: 'pointer' }}>
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => alternarAtivo(p)} title={p.ativo ? 'Desativar' : 'Ativar'}
                            style={{ background: '#fed7aa', color: '#c2410c', padding: 6, borderRadius: 4, border: 'none', cursor: 'pointer' }}>
                            <Power size={14} />
                          </button>
                          {ehAdmin && (
                            <button onClick={() => excluir(p)} title="Excluir"
                              style={{ background: '#fecaca', color: '#b91c1c', padding: 6, borderRadius: 4, border: 'none', cursor: 'pointer' }}>
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  {aberto && (
                    <tr style={{ background: '#f9fafb' }}>
                      <td></td>
                      <td colSpan={6} style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
                          {produtos.length === 0 ? (
                            <div style={{ color: '#888', fontStyle: 'italic', fontSize: 13 }}>Carregando produtos…</div>
                          ) : produtos.map((prod) => (
                            <div key={prod.ordem} style={{ display: 'flex', gap: 10, alignItems: 'center', background: 'white', padding: 8, borderRadius: 6, border: '1px solid #e5e7eb' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', background: 'var(--primary-soft)', color: 'var(--primary)', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                                {prod.ordem}
                              </div>
                              {prod.foto_url
                                ? <FotoProduto url={prod.foto_url} tam={40} style={{ flexShrink: 0 }} />
                                : <div style={{ width: 40, height: 40, background: '#f3f4f6', borderRadius: 4, flexShrink: 0 }} />}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12, fontWeight: 500, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {prod.descricao}
                                </div>
                                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                                  Cod {prod.codigo_interno} · <span style={{ color: '#15803d', fontWeight: 600 }}>{formatMoeda(prod.preco_pix)}</span> · estoque {Number(prod.estoque_atual).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {lista.length === 0 && !carregando && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 20, color: '#888', fontStyle: 'italic' }}>Nenhuma promocao cadastrada.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modalAberto && (
        <PromocaoFormModal
          id={editandoId}
          grupos={grupos}
          onFechar={() => setModalAberto(false)}
          onSalvo={() => { setModalAberto(false); carregar(); }}
        />
      )}

      <Modal
        aberto={testePromo !== null}
        onFechar={() => setTestePromo(null)}
        titulo="Enviar fly de teste"
        largura={440}
        rodape={
          <>
            <button type="button" className="btn-secundario" onClick={() => setTestePromo(null)}>Cancelar</button>
            <button type="button" className="btn-primario" onClick={enviarTeste}
              disabled={enviandoTeste || numeroTeste.replace(/\D/g, '').length < 10}>
              {enviandoTeste ? 'Enviando...' : 'Enviar'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 13, color: '#4b5563', margin: 0 }}>
            Manda o fly de <b>{testePromo?.nome}</b> direto pra um numero de WhatsApp, na hora.
            Funciona ate com numero da equipe — serve pra voce conferir como o cliente recebe.
          </p>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: '#6b7280', marginBottom: 4 }}>
              WhatsApp do destinatario
            </label>
            <WhatsAppPickerInput
              value={numeroTeste}
              onChange={setNumeroTeste}
              placeholder="Buscar contato ou digitar numero..."
            />
          </div>
        </div>
      </Modal>

      <Modal
        aberto={previewImg !== null}
        onFechar={() => setPreviewImg(null)}
        titulo={previewTitulo || 'Pre-visualizacao'}
        largura={680}
      >
        {previewImg && (
          <div style={{ textAlign: 'center' }}>
            <img src={`data:image/jpeg;base64,${previewImg}`} alt="Pre-visualizacao"
              style={{ maxWidth: '100%', height: 'auto', borderRadius: 8, border: '1px solid #e5e7eb' }} />
            <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
              Esta e a imagem enviada ao cliente no WhatsApp.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
